import { useState, useEffect } from 'react';
import api from '../services/api';
import { ShippingOrder } from '../types';
import ScanOutModal from '../components/ScanOutModal';
import PackageSetupModal from '../components/PackageSetupModal';

export default function WillCallPage() {
  const [orders, setOrders] = useState<ShippingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanOutOrderId, setScanOutOrderId] = useState<string | null>(null);
  const [packageSetupOrder, setPackageSetupOrder] = useState<{ id: string; order_number: string } | null>(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await api.getWillCallQueue();
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyReady = async (order: ShippingOrder) => {
    if (!confirm('Send notification that order is ready for pickup?')) return;
    try {
      await api.notifyWillCallReady(order.id);
      alert('Notification sent successfully');
      loadOrders();
    } catch (error) {
      console.error('Failed to send notification:', error);
      alert(error instanceof Error ? error.message : 'Failed to send notification');
    }
  };

  const handleScanOutComplete = async (pickedUpBy?: string) => {
    if (!scanOutOrderId || !pickedUpBy) return;
    try {
      await api.markWillCallPickedUp({
        order_id: scanOutOrderId,
        picked_up_by: pickedUpBy,
      });
      setScanOutOrderId(null);
      loadOrders();
    } catch (error) {
      console.error('Failed to mark picked up:', error);
      alert(error instanceof Error ? error.message : 'Failed to mark picked up');
    }
  };

  const hasBalanceDue = (order: ShippingOrder) => {
    const total = parseFloat(String(order.total_amount || 0));
    const paid = parseFloat(String(order.payment_amount || 0));
    return total > 0 && paid < total - 0.01;
  };

  const hasShortage = (order: ShippingOrder) => !!(order as any).shortage_hold;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Will Call Queue</h1>
          <p className="text-gray-400 mt-1">Orders ready for customer pickup</p>
        </div>
        <button
          onClick={loadOrders}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No Will Call Orders</h3>
          <p className="text-gray-400">There are no orders waiting for customer pickup.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {orders.map((order) => {
            const hasBalance = hasBalanceDue(order);
            const shortage = hasShortage(order);
            const isBlocked = hasBalance || shortage;
            return (
            <div key={order.id} className={`bg-gray-800 rounded-xl p-6 ${isBlocked ? 'border-2 border-red-500' : ''}`}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{order.order_number}</h3>
                    <span className="px-2 py-1 text-xs rounded-full bg-yellow-900 text-yellow-300">
                      Will Call
                    </span>
                    {hasBalance ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-red-900 text-red-300 font-bold">
                        BALANCE DUE
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-900 text-green-300">
                        PAID IN FULL
                      </span>
                    )}
                    {shortage && (
                      <span className="px-2 py-1 text-xs rounded-full bg-red-900 text-red-300 font-bold animate-pulse">
                        PARTS SHORTAGE
                      </span>
                    )}
                  </div>

                  {hasBalance && (
                    <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 mb-3">
                      <p className="text-red-300 text-sm font-semibold">
                        DO NOT RELEASE — Payment not complete
                      </p>
                      <p className="text-red-400 text-xs mt-1">
                        Order must be paid in full before pickup. Contact the office if the customer says they have paid.
                      </p>
                    </div>
                  )}
                  {shortage && (
                    <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 mb-3">
                      <p className="text-red-300 text-sm font-semibold">
                        DO NOT RELEASE — Parts shortage (Red Bin)
                      </p>
                      <p className="text-red-400 text-xs mt-1">
                        This order has missing parts. Resolve the shortage in Factory Admin before releasing.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Dealer</p>
                      <p className="text-white">{order.dealer_company}</p>
                      <p className="text-gray-400">{order.dealer_first_name} {order.dealer_last_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Contact</p>
                      <p className="text-white">{order.dealer_email}</p>
                      <p className="text-gray-400">{order.dealer_phone}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Order Details</p>
                      <p className="text-white">{order.unit_count || 0} units</p>
                      <p className="text-gray-400">Created: {new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setPackageSetupOrder({ id: order.id, order_number: order.order_number })}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                  >
                    Packages
                  </button>
                  <button
                    onClick={() => handleNotifyReady(order)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Notify Ready
                  </button>
                  <button
                    onClick={() => isBlocked
                      ? alert(hasBalance ? 'Cannot release — payment not complete. Full payment required before pickup.' : 'Cannot release — parts shortage hold is active. Resolve shortages in Factory Admin first.')
                      : setScanOutOrderId(order.id)}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      isBlocked
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                    Scan Out & Release
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Scan-Out Modal */}
      {scanOutOrderId && (
        <ScanOutModal
          orderId={scanOutOrderId}
          scanType="will_call"
          onComplete={handleScanOutComplete}
          onClose={() => setScanOutOrderId(null)}
        />
      )}

      {/* Package Setup Modal */}
      {packageSetupOrder && (
        <PackageSetupModal
          orderId={packageSetupOrder.id}
          orderNumber={packageSetupOrder.order_number}
          onClose={() => setPackageSetupOrder(null)}
          onComplete={() => {
            setPackageSetupOrder(null);
            loadOrders();
          }}
        />
      )}
    </div>
  );
}
